import { Dialog } from "radix-ui";
import { Tabs } from "radix-ui";
import Button from "@/components/bricks/button/Button";
import { Dispatch, SetStateAction, useContext, useRef, useState } from "react";
import { patch } from "../../../api/util";
import {
  QueryFilter,
  FilterPreset,
  FilterKeys,
} from "../../../interfaces/filters";
import { getPatchOperations } from "../../../util/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../select/Select";
import { DEFAULT_ERROR } from "../../../constants/errors";
import {
  FilterPresetsContext,
  FilterPresetsEventContext,
} from "../../../context/FiltersContext";
import useLocalStorageState from "use-local-storage-state";
import IsRequired from "../../formcontrols/common/IsRequired";
import InputErrorMessage from "../../InputErrorMessage";
import { UserQueryContext } from "../../../context/UserContext";
import InputCheckbox from "../../formcontrols/InputCheckbox";
import { orderBy } from "lodash";

const FiltersSaveTabOverskrivFilter = (props: {
  contextType: FilterPreset["type"];
  setOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  // props
  const { contextType, setOpen } = props;

  // context
  const savedFilters = useContext(FilterPresetsContext);
  const savedFiltersEvent = useContext(FilterPresetsEventContext);
  const { user } = useContext(UserQueryContext);

  // case gymnastics
  // lowercase context type to match the correct key in localStorage
  const contextCased =
    contextType.charAt(0).toLowerCase() + contextType.slice(1);

  // state and storage
  const [selected, setSelected] = useState<FilterPreset | null>(null);
  const [checked, setChecked] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [errors, setErrors] = useState<string | null>(null);
  const [filters] = useLocalStorageState<QueryFilter<FilterKeys>[]>(
    `${contextCased}Filters`,
  );

  // sort things
  const sortedPersonalFilters = orderBy(
    savedFilters.flatMap((filter) => (filter.erPersonlig ? [filter] : [])),
    ["navn"],
    ["asc"],
  );
  const sortedPublicFilters = orderBy(
    savedFilters.flatMap((filter) => (!filter.erPersonlig ? [filter] : [])),
    ["navn"],
    ["asc"],
  );

  // refs
  const inputRef = useRef<HTMLInputElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);

  // handlers
  function onSelectValueChange(value: string) {
    const filter = savedFilters.find((filter) => filter.navn === value);

    if (filter) {
      setChecked(!filter.erPersonlig);
      setName(filter.navn);
      setSelected(filter);
    } else {
      setSelected(null);
    }
  }

  async function updateFilterHandler() {
    const personal = !checkboxRef.current?.checked;

    if (name) {
      const nameExists = savedFilters.some((filter) => filter.navn === name);
      if (nameExists) {
        setErrors("Filternavn er allerede i brug.");
        return;
      }
    } else {
      setErrors("Filternavn mangler");
      return;
    }

    if (personal !== undefined && selected) {
      setErrors(null);
      try {
        const updatedFilter: Omit<SoegefilterDTO, "id" | "type"> = {
          filter: JSON.stringify(filters),
          navn: name.trim(),
          erPersonlig: personal,
        };

        const result = await patch<SoegefilterDTO>({
          query: `brugere/${user?.id}/soegefiltre/${selected?.id}`,
          body: JSON.stringify(getPatchOperations(updatedFilter)),
        });

        const filterForLocal = [
          ...savedFilters.filter((filter) => result.id !== filter.id),
          { ...result, filter: JSON.parse(result.filter) },
        ];

        savedFiltersEvent({
          event: "set",
          filters: filterForLocal,
        });
      } catch (error: any) {
        if (error?.data === typeof "string") {
          setErrors(error.data);
        } else {
          setErrors(DEFAULT_ERROR.message);
        }
        throw error;
      }
      setOpen(false);
    } else {
      setErrors(DEFAULT_ERROR.message);
    }
  }

  return (
    <Tabs.Content value="overskrivFilter">
      <div className="c-item-list p-3">
        <div className="flex-100 mt-2">
          <label className="form-label" htmlFor="saveNameReplace">
            Vælg filter som skal overskrives
            <IsRequired />
          </label>
          <Select
            onValueChange={onSelectValueChange}
            defaultValue={selected?.navn ?? undefined}
          >
            <SelectTrigger
              aria-label="Vælg filter"
              placeholder="Vælg filter ..."
            />
            <SelectContent>
              <div className="c-dropdown-menu-label mx-1 px-4">Mine filtre</div>
              {sortedPersonalFilters.map((filter) => (
                <SelectItem key={filter.id} value={filter.navn}>
                  <div className="d-flex flex-row align-items-center justify-space-between">
                    <div className="flex-grow-1 text">{filter.navn}</div>
                  </div>
                </SelectItem>
              ))}
              <hr />
              <div className="c-dropdown-menu-label mx-1 px-4">
                Standardfiltre
              </div>
              {sortedPublicFilters.map((filter) => (
                <SelectItem key={filter.id} value={filter.navn}>
                  <div className="d-flex flex-row align-items-center justify-space-between">
                    <div className="flex-grow-1 text">{filter.navn}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-100">
          <label className="form-label" htmlFor="saveNameReplace">
            Gem som
            <IsRequired />
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Indtast nyt navn ..."
            ref={inputRef}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="flex-100 d-block">
          <h5 className="form-label mb-2 d-block">Filtertype</h5>
          <InputCheckbox
            id="filter-preset-type-check"
            label="Standardfilter"
            checked={checked}
            disabled={selected ? !selected?.erPersonlig : false}
            ref={checkboxRef}
            onChange={(event) => setChecked(event.currentTarget.checked)}
          />
        </div>
      </div>

      {errors && (
        <InputErrorMessage
          className="px-3"
          id="feedback"
          value={`*${errors}`}
        />
      )}

      <div className="flex-100 p-3 d-flex justify-content-between gap-2">
        <Dialog.Close asChild>
          <Button color="secondary">Annuller</Button>
        </Dialog.Close>
        <button
          className="btn btn-primary"
          onClick={updateFilterHandler}
          disabled={selected ? false : true}
        >
          Overskriv
        </button>
      </div>
    </Tabs.Content>
  );
};

export default FiltersSaveTabOverskrivFilter;
